// src/pages/Admin/Coaching/CoachingHoursChart.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
} from "recharts";
import { getAllEnseignants } from "../../../services/adherentServices";
import { getTeacherEvents } from "../../../services/eventServices";

const frHours = (v) =>
  Number(v || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 }) + " h";

const CustomTooltip = ({ active, payload, label, isDark }) => {
  if (!active || !payload?.length) return null;
  const val = payload[0].value;
  return (
    <div
      className={`rounded-xl border px-3 py-2 shadow-sm ${
        isDark
          ? "bg-slate-900 border-slate-700 text-slate-100"
          : "bg-white border-slate-200 text-slate-800"
      }`}
    >
      <div className="text-[11px] opacity-70">{label}</div>
      <div className="text-sm font-semibold">{frHours(val)}</div>
    </div>
  );
};

export default function CoachingHoursChart() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDark, setIsDark] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false
  );

  useEffect(() => {
    // suivre le changement de thème (si tu toggles "dark" sur <html>)
    if (typeof MutationObserver === "undefined") return;
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains("dark"))
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const teachers = await getAllEnseignants();
        const rows = await Promise.all(
          teachers.map(async (t) => {
            const events = await getTeacherEvents(t.id);
            const total =
              (events?.Events || []).reduce(
                (acc, ev) => acc + (Number(ev?.AdherentEvent?.coach_hour) || 0),
                0
              ) || 0;
            return { nom: `${t.nom} ${t.prenom}`, hours: Number(total.toFixed(2)) };
          })
        );
        // tri décroissant par heures
        rows.sort((a, b) => b.hours - a.hours);
        setData(rows);
      } catch (e) {
        console.error("Erreur récupération heures coaching", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="h-[320px] w-full animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-900/40" />
    );
  }

  const grid = isDark ? "#334155" : "#e2e8f0";
  const tick = isDark ? "#cbd5e1" : "#334155";
  const cursorFill = isDark ? "rgba(148,163,184,0.12)" : "rgba(148,163,184,0.15)";
  const topColor = isDark ? "#9CA3FF" : "#A5B4FC"; // indigo pastel
  const bottomColor = isDark ? "#6C72D9" : "#818CF8";

  return (
    <div className="w-full h-[320px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{ top: 20, right: 16, left: 4, bottom: 40 }}
        >
          <defs>
            <linearGradient id="barFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={topColor} stopOpacity={0.95} />
              <stop offset="100%" stopColor={bottomColor} stopOpacity={0.85} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke={grid} />
          <XAxis
            dataKey="nom"
            height={40}
            interval={0}
            angle={-25}
            dy={20}
            tick={{ fill: tick, fontSize: 12 }}
          />
          <YAxis
            tick={{ fill: tick, fontSize: 12 }}
            width={40}
            tickFormatter={(v) => Number(v).toLocaleString("fr-FR")}
          />

          <Tooltip
            cursor={{ fill: cursorFill }}
            content={<CustomTooltip isDark={isDark} />}
          />

          {/* Légende non indispensable -> lisibilité plus clean, on affiche les labels directement */}
          <Bar dataKey="hours" fill="url(#barFill)" radius={[8, 8, 0, 0]}>
            <LabelList
              dataKey="hours"
              position="top"
              formatter={(v) =>
                Number(v).toLocaleString("fr-FR", { maximumFractionDigits: 2 })
              }
              style={{ fill: isDark ? "#e2e8f0" : "#0f172a", fontSize: 12 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
